import { useContext, useEffect, useState } from 'react';
import {
  Banner,
  Buttons,
  FlexContainer,
  Icon,
  ModalContext,
  Sizes,
  Spinner,
  Text,
} from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import { PueblaPreviousYearsResponse } from 'Services/sat/types';
import useTranslation from 'Hooks/useTranslation';
import { useFetchGetPreviousYearsPercentage } from 'Containers/PreviousYears/hooks';
import YearsBanner from 'Containers/PreFiling/components/YearsBanner';
import YearsModal from 'Containers/PreFiling/components/YearsModal';
import CompensationBox from 'Containers/PreFiling/components/CompensationBox';
import { ResultType } from 'Types/common';
import * as Styled from './styles';
import { calculateAmount } from './utils';

const PLAN_PRO_AMOUNT = 549;
const COMPENSATION_AND_PAY_CONTEXT = 'compensationAndPay';

export const YearsInfoCompensation = ({
  resultInfo,
  data,
}: {
  resultInfo: ResultType;
  data: PueblaPreviousYearsResponse;
}): JSX.Element => {
  const { t } = useTranslation(locizeNS.FILING);
  const [compensationState, setCompensationState] = useState({
    compensation: false,
    isCompensationAndPay: false,
    isZeroCompensation: false,
    amountToRecover: 0,
  });
  const { showModal } = useContext(ModalContext);
  const [fee, setFee] = useState(0);
  const { mutate, isLoading, data: price } = useFetchGetPreviousYearsPercentage();
  const records = data?.records;

  const isCompensationAndPayInfo =
    Math.abs(data?.amountPending ?? 0) < Math.abs(resultInfo?.taxReturnResult ?? 0);
  const isZeroAmount =
    Math.abs(data?.amountPending ?? 0) === Math.abs(resultInfo?.taxReturnResult ?? 0);

  useEffect(() => {
    if (data) {
      const initialAmount = Math.abs(data.amountPending ?? 0);
      setCompensationState({
        compensation: false,
        isCompensationAndPay: false,
        isZeroCompensation: false,
        amountToRecover: initialAmount,
      });
    }
  }, [data]);

  useEffect(() => {
    const amountToRecoverage =
      Math.abs(data?.amountPending ?? 0) - Math.abs(resultInfo?.taxReturnResult ?? 0);

    setCompensationState({
      compensation: true,
      isCompensationAndPay: isCompensationAndPayInfo,
      isZeroCompensation: isZeroAmount,
      amountToRecover: amountToRecoverage,
    });
    mutate({ amount: calculateAmount(Math.abs(amountToRecoverage)) });
  }, []);

  useEffect(() => {
    if (price?.data?.item?.discountedPrice != null) {
      const newFee = Number(price.data.item.discountedPrice) / 100;
      setFee(newFee);
    }
  }, [price?.data?.item?.discountedPrice]);

  const handleClickTiny = (): void => {
    showModal({
      comp: <YearsModal />,
    });
  };

  const recordsRecoverables = records
    ?.filter(record => record.isRecoverable)
    .sort((a, b) => Number(a.taxYear) - Number(b.taxYear));

  const handleManageCompensation = (): void => {
    if (data?.amountPending) {
      const amountToRecoverage =
        Math.abs(data?.amountPending ?? 0) - Math.abs(resultInfo?.taxReturnResult ?? 0);
      setCompensationState({
        compensation: true,
        isCompensationAndPay: isCompensationAndPayInfo,
        isZeroCompensation: isZeroAmount,
        amountToRecover: amountToRecoverage,
      });
      mutate({ amount: calculateAmount(amountToRecoverage) });
    }
  };

  const handleNotCompensation = (): void => {
    if (data?.amountPending) {
      setCompensationState({
        compensation: !compensationState.compensation,
        isCompensationAndPay: false,
        isZeroCompensation: false,
        amountToRecover: data.amountPending,
      });
      mutate({ amount: calculateAmount(data.amountPending) });
    }
  };

  const isCompensationAndPay =
    compensationState.isCompensationAndPay || compensationState.isZeroCompensation;

  const columns = (recordsRecoverables?.length ?? 0) + 1;
  return (
    <FlexContainer direction="column" fullWidth align="center" gap="dimension_16">
      <Styled.YearsContent
        direction="column"
        gap="dimension_08"
        fullWidth
        justify="center"
        align="center"
      >
        <Text type="paragraphs" color="grey" size="XS_M_S">
          {t('PreFiling.years.info')}
        </Text>
        <Styled.YearsContainer
          justify="center"
          align="center"
          gap="dimension_08"
          items={columns.toString()}
        >
          {recordsRecoverables?.map(record => (
            <Styled.YearsInfoContainer
              justify="center"
              align="center"
              direction="column"
              gap="dimension_08"
              $itemsLength={recordsRecoverables?.length}
              $isCompensation={compensationState.compensation}
              key={record.id}
            >
              <Text type="paragraphs" color="dark" size="S_M_S">
                {record.taxYear}
              </Text>
              <Text type="paragraphs" color="success_strong" size="S_L_S">
                {YayoyakiFormatter.formatAmountMxn(Math.abs(Number(record.amountTotal)))}
              </Text>
            </Styled.YearsInfoContainer>
          ))}
          <CompensationBox
            resultInfo={resultInfo?.taxReturnResult}
            compensation={compensationState.compensation}
            handleCompensation={handleManageCompensation}
            handleNotCompensation={handleNotCompensation}
          />
        </Styled.YearsContainer>
        <Styled.TotalRecoveredContainer
          justify="space-between"
          align="center"
          $isCompensationAndPay={compensationState.isCompensationAndPay}
          $isZeroCompensation={compensationState.isZeroCompensation}
        >
          <Text
            type="paragraphs"
            color={isCompensationAndPay ? 'dark' : 'success_strong'}
            size="S_L_S"
          >
            {t('PreFiling.years.amount.total', {
              context: compensationState.isCompensationAndPay && 'toPay',
            })}
          </Text>
          <Styled.TextInfo
            type="paragraphs"
            $isCompensationAndPay={compensationState.isCompensationAndPay}
            $isZeroCompensation={compensationState.isZeroCompensation}
            size="S_L_S"
          >
            {YayoyakiFormatter.formatAmountMxn(compensationState.amountToRecover)}
          </Styled.TextInfo>
        </Styled.TotalRecoveredContainer>
        {!compensationState.compensation && (
          <Styled.BannerContainer align="center">
            <Banner
              label={t('PreFiling.years.compensation.banner')}
              mode="error"
              size={Sizes.M}
              icon={<Icon icon="WarningRoundedSolidXS" />}
              id="compensation-banner"
              hasIcon
              position="vertically"
              titleSize={Sizes.XS}
              layout="embed"
            />
          </Styled.BannerContainer>
        )}
      </Styled.YearsContent>
      {Boolean(!isCompensationAndPay) && (
        <Buttons.Tiny label={t('PreFiling.years.help.button')} onClick={handleClickTiny} />
      )}
      <Styled.PreviousYearsFeeContainer justify="space-between" align="center">
        <Text type="paragraphs" color="dark" size="S_L_S">
          {t('PreFiling.years.fee', {
            context: compensationState.isCompensationAndPay && COMPENSATION_AND_PAY_CONTEXT,
          })}
        </Text>
        {isLoading ? (
          <Spinner size="S" mode="primary" />
        ) : (
          <Text type="paragraphs" color="dark" size="M_XL_S">
            {YayoyakiFormatter.formatAmountMxn(
              isCompensationAndPay ? PLAN_PRO_AMOUNT : Math.abs(fee ?? 0)
            )}
          </Text>
        )}
      </Styled.PreviousYearsFeeContainer>
      <YearsBanner
        data={resultInfo}
        compensation={compensationState.compensation}
        isForCompensation
        isCompensationAndPay={isCompensationAndPay}
      />
    </FlexContainer>
  );
};
